import type { ReactNode } from 'react'
import { DEV_FLAGS, type DevFlagKey, type FlagKey } from '@/services/analytics/feature-flags'
import { useFeatureFlag, useFeatureFlagState } from '@/services/analytics/use-feature-flag'
import { useDevFlag } from '@/services/analytics/dev-flags'

/**
 * The in-layout counterpart of SurfaceGate. A panel saved into a layout while
 * its rollout flag was on must not keep rendering the surface once the flag
 * is off. This is not a plan lock: nothing here sells an upgrade, because no
 * plan turns a rollout flag on. The panel header (and its close control) still
 * renders around this, so the tile can be removed.
 */
export function RolloutGate({ flag, children }: { flag: FlagKey; children: ReactNode }) {
  const state = useFeatureFlagState(flag)
  if (state === 'loading') return null
  if (state === 'off') {
    return (
      <div className="flex h-full w-full items-center justify-center p-4 text-center">
        <p className="text-sm text-muted-foreground">This panel isn't available yet.</p>
      </div>
    )
  }
  return <>{children}</>
}

const DEV_FLAG_KEYS: ReadonlySet<string> = new Set(Object.values(DEV_FLAGS))

type FlagChildren = (on: boolean) => ReactNode

/**
 * The quiet gate for a flagged panel's chrome (title-bar actions, menu items):
 * renders `children(on)` and no notice. One flag is read, so only flagged panel
 * types subscribe; a dev flag reads through `useDevFlag` (off unless PostHog
 * says on), a rollout flag through `useFeatureFlag` (loading reads as off).
 */
export function RolloutFlag({
  flag,
  children,
}: {
  flag: FlagKey | DevFlagKey
  children: FlagChildren
}) {
  return DEV_FLAG_KEYS.has(flag) ? (
    <DevFlagValue flag={flag as DevFlagKey}>{children}</DevFlagValue>
  ) : (
    <RolloutFlagValue flag={flag as FlagKey}>{children}</RolloutFlagValue>
  )
}

function RolloutFlagValue({ flag, children }: { flag: FlagKey; children: FlagChildren }) {
  return <>{children(useFeatureFlag(flag))}</>
}

function DevFlagValue({ flag, children }: { flag: DevFlagKey; children: FlagChildren }) {
  return <>{children(useDevFlag(flag))}</>
}
